<template>
	<div v-if="churned.length || looksGoneWithNothingToRecord" class="cancellation-summary">
		<button
			v-for="sub in churned"
			:key="sub.id"
			type="button"
			class="reason-row"
			:class="{ empty: !hasReason(sub) }"
			:title="hasReason(sub) ? 'Edit this cancellation reason' : 'Record why they left'"
			@click="$emit('edit', sub)"
		>
			<span class="badge bg-danger">{{ statusLabel(sub.status) }}</span>
			<span class="brand">{{ brandLabel(sub) }}</span>
			<span class="sep">·</span>
			<span class="summary">{{ summaryOf(sub) }}</span>
			<font-awesome-icon :icon="['fas', hasReason(sub) ? 'pen' : 'plus']" class="ms-auto ic" />
		</button>

		<!-- An account can be gone with no subscription row to hang a reason on. Saying so beats
			rendering nothing, which reads as a bug. -->
		<div v-if="looksGoneWithNothingToRecord" class="reason-row muted">
			<span class="summary">No subscription to record a cancellation against</span>
		</div>
	</div>
</template>

<script>
/**
 * One compact row per cancelled brand, under Client status. It is a button: clicking opens the
 * same modal the reason was entered with, prefilled, so anyone can correct it. The detail lives in
 * the modal rather than on the card, because the card is a summary and the verbatim quote is long.
 */
export default {
	name: 'CancellationSummary',
	props: {
		subscriptions: { type: Array, default: () => [] },
		reasonList: { type: Array, default: () => [] },
		clientStatus: { type: String, default: '' },
		clientDeletedAt: { type: [String, Object], default: null },
	},
	computed: {
		churned() {
			const churned = ['lost', 'cancelled_after_trial', 'canceled']
			return (this.subscriptions || []).filter(s => churned.includes(s.status) && !s.superseded_by_id)
		},
		looksGoneWithNothingToRecord() {
			const gone = this.clientStatus === 'deleted' || !!this.clientDeletedAt
			return gone && !this.churned.length
		},
	},
	methods: {
		hasReason(sub) {
			return !!(sub.cancel_recorded_at || this.reasonCodes(sub).length || sub.cancel_reason)
		},
		reasonCodes(sub) {
			const raw = sub.cancel_reasons
			if (!raw) return []
			if (Array.isArray(raw)) return raw
			try {
				const parsed = JSON.parse(raw)
				return Array.isArray(parsed) ? parsed : []
			} catch (e) {
				return []
			}
		},
		/** the one line the card shows; everything else is a click away */
		summaryOf(sub) {
			if (!this.hasReason(sub)) return 'No reason recorded'
			const labels = this.reasonCodes(sub).map(c => this.reasonLabel(c))
			if (sub.lost_to_competitor) labels.push('to ' + sub.lost_to_competitor)
			return labels.length ? labels.join(', ') : 'Reason recorded'
		},
		reasonLabel(code) {
			const hit = (this.reasonList || []).find(r => r.code === code)
			return hit ? hit.label : code
		},
		brandLabel(sub) {
			const b = sub && sub.brand
			const key = typeof b === 'string' ? b : (b && b.key) || (sub && sub.brand_key)
			return { town_web: 'Town Web', heygov: 'HeyGov', clerkminutes: 'ClerkMinutes' }[key] || key || 'Subscription'
		},
		statusLabel(status) {
			return { lost: 'Lost', cancelled_after_trial: 'Cancelled', canceled: 'Cancelled' }[status] || status
		},
	},
}
</script>

<style scoped>
.cancellation-summary {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-top: 6px;
}
.reason-row {
	display: flex;
	align-items: center;
	gap: 6px;
	width: 100%;
	text-align: left;
	background: none;
	border: 1px solid transparent;
	border-radius: 6px;
	padding: 3px 6px;
	font-size: 0.82rem;
	color: #4a5462;
	cursor: pointer;
}
.reason-row:hover {
	background: #f4f9ff;
	border-color: #d6e6f7;
}
.reason-row.muted {
	cursor: default;
	color: #9aa2ad;
	font-style: italic;
}
.reason-row.muted:hover {
	background: none;
	border-color: transparent;
}
.reason-row .brand {
	font-weight: 600;
	color: #22292f;
}
.reason-row .sep {
	color: #c3cad3;
}
.reason-row .summary {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.reason-row.empty .summary {
	color: #c98a05;
}
.reason-row .ic {
	color: #9aa2ad;
	font-size: 0.72rem;
	flex: none;
}
</style>
